import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Icon from '../components/AppIcon';

const BlogListing = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTag, setSelectedTag] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isIndividualBlog, setIsIndividualBlog] = useState(false);
  const [allBlogs, setAllBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Dynamic blog loading using Vite's import.meta.glob
  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setIsLoading(true);
        
        // Use Vite's import.meta.glob to dynamically import all blog files
        const blogModules = import.meta.glob('../blogs/*.jsx');
        const blogs = [];
        
        // Load all blog files dynamically
        for (const path in blogModules) {
          try {
            const module = await blogModules[path]();
            // Get the exported blog object (assuming each file exports one blog object)
            const blogData = Object.values(module)[0];
            if (blogData && blogData.slug && blogData.title) {
              blogs.push(blogData);
            } else {
              console.warn(`Invalid blog data in ${path}:`, blogData);
            }
          } catch (error) {
            console.error(`Error loading blog from ${path}:`, error);
          }
        }
        
        // Sort blogs by publish date (newest first)
        blogs.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
        
        setAllBlogs(blogs);
      } catch (error) {
        console.error('Error loading blogs:', error);
        setAllBlogs([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadBlogs();
  }, []);

  // Handle individual blog page
  useEffect(() => {
    if (slug && allBlogs.length > 0) {
      const blog = allBlogs.find(b => b.slug === slug);
      if (blog) {
        setSelectedBlog(blog);
        setIsIndividualBlog(true);
      } else {
        navigate('/blog');
      }
    } else if (!slug) {
      setIsIndividualBlog(false);
      setSelectedBlog(null);
    }
  }, [slug, navigate, allBlogs]);

  // Show loading state
  if (isLoading) {
    return (
      <>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <div style={{ minHeight: '100vh', backgroundColor: '#f4faf8' }}>
          <Header />
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: 'calc(100vh - 105px)' // Adjust based on your header height
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', color: '#64748B', marginBottom: '20px' }}>Loading blogs...</div>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '4px solid #f1f5f9', 
                borderTop: '4px solid #00d690', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Helper functions
  const getAllCategories = () => {
    const categories = [...new Set(allBlogs.map(blog => blog.category))];
    return categories;
  };

  const getAllTags = () => {
    const tags = [...new Set(allBlogs.flatMap(blog => blog.tags))];
    return tags;
  };

  const categories = ['All', ...getAllCategories()];
  const tags = ['All', ...getAllTags()];
  const filteredBlogs = allBlogs.filter(blog => {
    const matchesCategory = selectedCategory === 'All' || blog.category === selectedCategory;
    const matchesTag = selectedTag === 'All' || blog.tags.includes(selectedTag);
    const matchesSearch = searchTerm === '' || 
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesCategory && matchesTag && matchesSearch;
  });

  // Enhanced SEO Data
  const seoData = {
    title: 'Blog - OctalChip | Web Development, Mobile Apps, AI & DevOps Insights',
    description: 'Read our latest insights on web development, mobile app development, AI integration, DevOps, and cloud infrastructure. Expert guides and best practices from OctalChip development team.',
    keywords: 'web development blog, mobile app development blog, AI development blog, DevOps blog, cloud infrastructure blog, software development insights, technology trends, development best practices, programming tutorials, tech blog',
    canonical: 'https://www.octalchip.com/blog',
    tags: ['web development blog', 'mobile app development', 'AI development', 'DevOps', 'cloud infrastructure'],
    breadcrumbs: [
      { name: 'Home', url: 'https://www.octalchip.com/' },
      { name: 'Blog', url: 'https://www.octalchip.com/blog' }
    ],
    faqData: [
      {
        question: 'What topics does the OctalChip blog cover?',
        answer: 'Our blog covers web development, mobile app development, AI integration, DevOps, cloud infrastructure, and technology trends with expert insights and best practices.'
      },
      {
        question: 'How often do you publish new blog posts?',
        answer: 'We publish new blog posts regularly, typically 2-3 articles per month covering the latest trends and technologies in software development.'
      },
      {
        question: 'Can I suggest topics for future blog posts?',
        answer: 'Yes! We welcome topic suggestions from our readers. You can contact us through our contact form or social media channels.'
      },
      {
        question: 'Are the blog posts written by experts?',
        answer: 'All our blog posts are written by our experienced development team members who have hands-on experience with the technologies and practices they write about.'
      }
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "OctalChip Development Blog",
      "description": "Expert insights on web development, mobile apps, AI integration, DevOps, and cloud infrastructure",
      "url": "https://www.octalchip.com/blog",
      "publisher": {
        "@type": "Organization",
        "name": "OctalChip",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.octalchip.com/favicon.svg"
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "404, Opera point, kothariya",
          "addressLocality": "Gujarat",
          "addressCountry": "IN",
          "postalCode": "360022"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "business@octalchip.com",
          "telephone": "+91-7574082582"
        }
      },
      "blogPost": allBlogs.map(blog => ({
        "@type": "BlogPosting",
        "headline": blog.title,
        "description": blog.shortDescription,
        "url": `https://www.octalchip.com/blog/${blog.slug}`,
        "datePublished": blog.publishDate,
        "dateModified": blog.publishDate,
        "author": {
          "@type": "Organization",
          "name": "OctalChip"
        },
        "publisher": {
          "@type": "Organization",
          "name": "OctalChip",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.octalchip.com/favicon.svg"
          }
        },
        "articleSection": blog.category,
        "keywords": blog.keywords,
        "wordCount": blog.readTime
      }))
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  // If we're on an individual blog page, render the blog detail view
  if (isIndividualBlog && selectedBlog) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f4faf8' }}>
        <Header />
        <SEO 
          title={selectedBlog.title}
          description={selectedBlog.metaDescription}
          keywords={selectedBlog.keywords}
          canonical={`https://www.octalchip.com/blog/${selectedBlog.slug}`}
          ogType="article"
          author="OctalChip"
          publishDate={selectedBlog.publishDate}
          modifiedDate={selectedBlog.publishDate}
          articleSection={selectedBlog.category}
          tags={selectedBlog.tags}
          breadcrumbs={[
            { name: 'Home', url: 'https://www.octalchip.com/' },
            { name: 'Blog', url: 'https://www.octalchip.com/blog' },
            { name: selectedBlog.title, url: `https://www.octalchip.com/blog/${selectedBlog.slug}` }
          ]}
          structuredData={{
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": selectedBlog.title,
            "description": selectedBlog.metaDescription,
            "datePublished": selectedBlog.publishDate,
            "dateModified": selectedBlog.publishDate,
            "author": {
              "@type": "Organization",
              "name": "OctalChip",
              "url": "https://www.octalchip.com"
            },
            "publisher": {
              "@type": "Organization",
              "name": "OctalChip",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.octalchip.com/favicon.svg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://www.octalchip.com/blog/${selectedBlog.slug}`
            },
            "articleSection": selectedBlog.category,
            "keywords": selectedBlog.keywords,
            "wordCount": selectedBlog.readTime,
            "inLanguage": "en-US"
          }}
        />
        
        {/* Individual Blog Page Content */}
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
          {/* Back Button */}
          <div style={{ marginBottom: '30px' }}>
            <a
              href="/blog"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: '#2d3748',
                textDecoration: 'none',
                fontSize: '16px',
                fontWeight: '500',
                padding: '8px 16px',
                borderRadius: '8px',
                backgroundColor: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#f7fafc';
                e.target.style.transform = 'translateY(-1px)';
                const arrowIcon = e.target.querySelector('svg');
                if (arrowIcon) {
                  arrowIcon.style.transform = 'translateX(-4px)';
                }
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.transform = 'translateY(0)';
                const arrowIcon = e.target.querySelector('svg');
                if (arrowIcon) {
                  arrowIcon.style.transform = 'translateX(0)';
                }
              }}
            >
              <Icon name="ArrowLeft" size={16} style={{ transition: 'transform 0.3s ease' }} />
              Back to Blog
            </a>
          </div>

          {/* Blog Header */}
          <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <span style={{
                backgroundColor: '#00d690',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                {selectedBlog.category}
              </span>
              <span style={{
                backgroundColor: '#f1f5f9',
                color: '#64748B',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {selectedBlog.readTime}
              </span>
              <span style={{
                backgroundColor: '#f1f5f9',
                color: '#64748B',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {formatDate(selectedBlog.publishDate)}
              </span>
            </div>

            <h1 style={{
              fontSize: '48px',
              fontWeight: 'bold',
              color: '#1E293B',
              marginBottom: '20px',
              lineHeight: '1.2'
            }}>
              {selectedBlog.title}
            </h1>

            <p style={{
              fontSize: '20px',
              color: '#64748B',
              lineHeight: '1.6',
              marginBottom: '30px'
            }}>
              {selectedBlog.shortDescription}
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginTop: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="Calendar" size={16} style={{ color: '#64748B' }} />
                <span style={{ fontSize: '16px', color: '#64748B' }}>
                  {formatDate(selectedBlog.publishDate)}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="Clock" size={16} style={{ color: '#64748B' }} />
                <span style={{ fontSize: '16px', color: '#64748B' }}>
                  {selectedBlog.readTime}
                </span>
              </div>
            </div>
          </div>

          {/* Blog Content */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            marginBottom: '40px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              lineHeight: '1.8',
              fontSize: '18px',
              color: '#374151'
            }}>
              {selectedBlog.content}
            </div>
          </div>

          {/* Internal and External Links */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
              {/* Internal Links */}
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1E293B', marginBottom: '20px' }}>
                  Related Services
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedBlog.internalLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      style={{
                        display: 'block',
                        padding: '12px 16px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        color: '#2d3748',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        border: '1px solid #e2e8f0'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#e2e8f0';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#f8fafc';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      {link.text}
                    </a>
                  ))}
                </div>
              </div>

              {/* External Links */}
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1E293B', marginBottom: '20px' }}>
                  External Resources
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedBlog.externalLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        padding: '12px 16px',
                        backgroundColor: '#f0f9ff',
                        borderRadius: '8px',
                        color: '#0369a1',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        border: '1px solid #bae6fd'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#e0f2fe';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#f0f9ff';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      {link.text} ↗
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Default blog listing page
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f4faf8' }}>
      <Header />
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        tags={seoData.tags}
        additionalMeta={seoData.additionalMeta}
        breadcrumbs={seoData.breadcrumbs}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'white', padding: '80px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{ fontSize: '48px', fontWeight: 'bold', color: '#1E293B', marginBottom: '24px' }}>
              Our Development Blog
            </h1>
            <p style={{ fontSize: '20px', color: '#64748B', marginBottom: '32px', lineHeight: '1.6' }}>
              Stay updated with the latest insights, trends, and best practices in web development, 
              mobile apps, AI integration, and DevOps from our expert development team.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#00d690' }}>
                <Icon name="BookOpen" size={20} />
                <span style={{ fontSize: '16px', fontWeight: '600' }}>{allBlogs.length} Articles</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#00d690' }}>
                <Icon name="Users" size={20} />
                <span style={{ fontSize: '16px', fontWeight: '600' }}>Expert Authors</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#00d690' }}>
                <Icon name="TrendingUp" size={20} />
                <span style={{ fontSize: '16px', fontWeight: '600' }}>Latest Trends</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section style={{ backgroundColor: 'white', padding: '40px 0', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Search Bar */}
            <div style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '16px 20px 16px 50px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#00d690';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 214, 144, 0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                  }}
                />
                <Icon 
                  name="Search" 
                  size={20} 
                  style={{ 
                    position: 'absolute', 
                    left: '16px', 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    color: '#64748B',
                    pointerEvents: 'none'
                  }} 
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute',
                      right: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px',
                      color: '#64748B',
                      transition: 'color 0.3s ease'
                    }}
                    onMouseOver={(e) => e.target.style.color = '#374151'}
                    onMouseOut={(e) => e.target.style.color = '#64748B'}
                  >
                    <Icon name="X" size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Category and Tag Filters */}
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {/* Category Filter */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    cursor: 'pointer',
                    minWidth: '150px'
                  }}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Tag Filter */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Tag</label>
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    cursor: 'pointer',
                    minWidth: '150px'
                  }}
                >
                  {tags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Cards Section */}
      <section style={{ backgroundColor: '#f8fafc', padding: '80px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          {filteredBlogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
                <Icon name="Search" size={48} style={{ color: '#cbd5e1' }} />
                <div>
                  <h3 style={{ fontSize: '24px', fontWeight: '600', color: '#475569', marginBottom: '12px' }}>
                    No articles found
                  </h3>
                  <p style={{ color: '#64748B', marginBottom: '24px', fontSize: '16px' }}>
                    Try adjusting your search criteria or browse all articles
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                  setSelectedTag('All');
                }}
                style={{
                  backgroundColor: '#00d690',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#00b876'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#00d690'}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '32px' }}>
                <h2 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1E293B', marginBottom: '8px' }}>
                  {filteredBlogs.length} Article{filteredBlogs.length !== 1 ? 's' : ''} Found
                </h2>
                <p style={{ color: '#64748B' }}>
                  {selectedCategory !== 'All' && `Category: ${selectedCategory}`}
                  {selectedCategory !== 'All' && selectedTag !== 'All' && ' • '}
                  {selectedTag !== 'All' && `Tag: ${selectedTag}`}
                  {searchTerm && ` • Search: "${searchTerm}"`}
                </p>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
                gap: '32px' 
              }}>
                {filteredBlogs.map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/blog/${blog.slug}`}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      textDecoration: 'none',
                      color: 'inherit'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    }}
                  >

                      {/* Blog Header */}
                      <div style={{ padding: '24px 24px 16px 24px' }}>
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                          <span style={{
                            backgroundColor: '#00d690',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {blog.category}
                          </span>
                          <span style={{
                            backgroundColor: '#f1f5f9',
                            color: '#64748B',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {blog.readTime}
                          </span>
                        </div>

                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: 'bold',
                          color: '#1E293B',
                          marginBottom: '12px',
                          lineHeight: '1.4',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {blog.title}
                        </h3>

                        <p style={{
                          color: '#64748B',
                          fontSize: '14px',
                          lineHeight: '1.6',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          marginBottom: '16px'
                        }}>
                          {blog.shortDescription}
                        </p>
                      </div>

                      {/* Blog Footer */}
                      <div style={{
                        padding: '16px 24px 24px 24px',
                        marginTop: 'auto',
                        borderTop: '1px solid #f1f5f9'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Icon name="Calendar" size={16} style={{ color: '#64748B' }} />
                            <span style={{ fontSize: '14px', color: '#64748B' }}>
                              {formatDate(blog.publishDate)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Icon name="Clock" size={16} style={{ color: '#64748B' }} />
                            <span style={{ fontSize: '14px', color: '#64748B' }}>
                              {blog.readTime}
                            </span>
                          </div>
                        </div>

                        {/* Tags */}
                        <div style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {blog.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              style={{
                                backgroundColor: '#f8fafc',
                                color: '#475569',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '500'
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                          {blog.tags.length > 3 && (
                            <span style={{
                              backgroundColor: '#f8fafc',
                              color: '#475569',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}>
                              +{blog.tags.length - 3} more
                            </span>
                          )}
                        </div>

                        {/* Read More Button */}
                        <div style={{ marginTop: '16px', textAlign: 'center' }}>
                          <span 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/blog/${blog.slug}`);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              color: '#00d690',
                              fontWeight: '600',
                              fontSize: '14px',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              textDecoration: 'none'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.color = '#00b876';
                              const arrowIcon = e.target.querySelector('svg');
                              if (arrowIcon) {
                                arrowIcon.style.transform = 'translateX(4px)';
                              }
                            }}
                            onMouseOut={(e) => {
                              e.target.style.color = '#00d690';
                              const arrowIcon = e.target.querySelector('svg');
                              if (arrowIcon) {
                                arrowIcon.style.transform = 'translateX(0)';
                              }
                            }}
                          >
                            Read More
                            <Icon name="ArrowRight" size={16} style={{ marginLeft: '4px', transition: 'transform 0.3s ease' }} />
                          </span>
                        </div>
                      </div>
                    </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ backgroundColor: 'white', padding: '80px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: '#1E293B', marginBottom: '24px' }}>
              Ready to Start Your Next Project?
            </h2>
            <p style={{ fontSize: '20px', color: '#64748B', marginBottom: '32px' }}>
              Our development team is ready to help you build amazing web applications, mobile apps, 
              and AI-powered solutions. Let's discuss your project requirements.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href="/services"
                style={{
                  backgroundColor: '#00d690',
                  color: 'white',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  transition: 'background-color 0.3s',
                  display: 'inline-block'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#00b876'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#00d690'}
              >
                View Our Services
              </a>
              <a
                href="/contact"
                style={{
                  backgroundColor: 'transparent',
                  color: '#00d690',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  border: '2px solid #00d690',
                  transition: 'all 0.3s',
                  display: 'inline-block'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#00d690';
                  e.target.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#00d690';
                }}
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogListing;
